import { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'

const variants = {
  primary: `
    bg-siglat-teal text-surface-base font-semibold
    hover:bg-siglat-teal-light active:bg-siglat-teal-dark
    shadow-teal-glow hover:shadow-teal-glow
  `,
  secondary: `
    bg-surface-overlay text-ink-primary font-medium border border-surface-border
    hover:bg-surface-raised hover:border-surface-border-light
  `,
  ghost: `
    bg-transparent text-ink-secondary font-medium
    hover:bg-surface-overlay hover:text-ink-primary
  `,
  danger: `
    bg-siglat-red/15 text-siglat-red font-semibold border border-siglat-red/25
    hover:bg-siglat-red/25
  `,
  outline: `
    bg-transparent border border-siglat-teal text-siglat-teal font-semibold
    hover:bg-siglat-teal/10
  `,
  // White-on-dark for social login buttons
  social: `
    bg-surface-overlay text-ink-primary font-medium border border-surface-border
    hover:bg-surface-raised hover:border-surface-border-light
    flex items-center justify-center gap-3
  `,
}

const sizes = {
  sm:  'h-9  px-4  text-sm  rounded-sm',
  md:  'h-11 px-5  text-sm  rounded',
  lg:  'h-12 px-6  text-base rounded-md',
  xl:  'h-14 px-8  text-base rounded-lg',
  // Full-width tall — primary CTA on mobile
  cta: 'h-14 px-6  text-base rounded-lg w-full',
}

const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    className = '',
    leftIcon,
    rightIcon,
    type = 'button',
    ...props
  },
  ref
) {
  const isDisabled = disabled || loading

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={[
        // Base
        'relative inline-flex items-center justify-center gap-2',
        'transition-all duration-150 ease-out',
        'select-none cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        // Variant
        variants[variant] ?? variants.primary,
        // Size
        sizes[size] ?? sizes.md,
        // Width
        fullWidth ? 'w-full' : '',
        // External
        className,
      ].join(' ')}
      {...props}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : leftIcon ? (
        <span className="flex-shrink-0">{leftIcon}</span>
      ) : null}

      <span className={loading ? 'opacity-0' : ''}>{children}</span>

      {!loading && rightIcon && (
        <span className="flex-shrink-0">{rightIcon}</span>
      )}
    </button>
  )
})

export default Button
